import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User as FirebaseUser, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { User, UserRole } from '../types';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  adminUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
  isFirstAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [adminUser, setAdminUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFirstAdmin, setIsFirstAdmin] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔍 Auth state changed:', user ? `User ${user.email} authenticated` : 'No user');
      setCurrentUser(user);
      
      if (user) {
        try {
          console.log('🔍 Fetching user data from Firestore for UID:', user.uid);
          // Get user data from Firestore
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          console.log('📊 User document exists:', userDoc.exists());
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log('📝 User data retrieved:', userData);
            
            // Check if user has admin role
            if (userData.role !== UserRole.ADMIN) {
              console.log('❌ User does not have admin privileges, signing out');
              await signOut(auth);
              setAdminUser(null);
              setIsFirstAdmin(false);
              return;
            }
            
            const userObj: User = {
              id: userDoc.id,
              email: userData.email,
              firstName: userData.firstName,
              lastName: userData.lastName,
              phone: userData.phone,
              role: userData.role,
              createdAt: userData.createdAt.toDate(),
              isActive: userData.isActive,
              status: userData.status || 'active',
              trackingToken: userData.trackingToken,
              agencyId: userData.agencyId,
              agencyAddress: userData.agencyAddress,
              agencyCity: userData.agencyCity,
              agencyPostalCode: userData.agencyPostalCode,
              agencySiren: userData.agencySiren,
              agencyLatitude: userData.agencyLatitude,
              agencyLongitude: userData.agencyLongitude,
              agencyCoverageAreas: userData.agencyCoverageAreas,
              commissionRatePerLead: userData.commissionRatePerLead,
            };
            
            setAdminUser(userObj);
            console.log('✅ Admin user set:', userObj);
            
            // Check if this is the first admin
            if (userData.role === UserRole.ADMIN) {
              console.log('🔍 Checking if this is the first admin...');
              const adminUsers = await getDoc(doc(db, 'admin_config', 'first_admin'));
              if (!adminUsers.exists()) {
                console.log('✅ This is the first admin');
                setIsFirstAdmin(true);
              }
              
              // Redirect to dashboard after successful admin authentication
              console.log('🚀 Redirecting to admin dashboard...');
              // The original code had useNavigate here, which is removed.
              // For now, we'll just log the navigation.
              // In a real application, you might use a state or context to manage navigation.
            }
          } else {
            console.log('❌ User document not found, signing out');
            await signOut(auth);
            setAdminUser(null);
            setIsFirstAdmin(false);
          }
        } catch (error) {
          console.error('❌ Error fetching user data:', error);
          console.error('❌ Error details:', {
            message: error instanceof Error ? error.message : String(error),
            code: (error as any)?.code || 'unknown'
          });
          // Don't fail authentication if Firestore access fails
          setAdminUser(null);
        }
      } else {
        setAdminUser(null);
        setIsFirstAdmin(false);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email: string, password: string) => {
    try {
      console.log('🔐 Attempting login for:', email);
      const result = await signInWithEmailAndPassword(auth, email, password);
      console.log('✅ Login successful for UID:', result.user.uid);
      
      // Don't check admin role here - let onAuthStateChanged handle it
      // The user will be automatically signed out if they don't have admin privileges
      // by the onAuthStateChanged listener
      
    } catch (error) {
      console.error('❌ Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value: AuthContextType = {
    currentUser,
    adminUser,
    loading,
    login,
    logout,
    isAdmin: adminUser?.role === UserRole.ADMIN,
    isFirstAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};



